/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface FloatingBill {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  speed: number;
  rotationSpeed: number;
  scaleType: 'shrink' | 'grow';
  baseScale: number;
  currentScale: number;
  scaleSpeed: number;
  pulsePhase: number;
  hasPulsed: boolean;
  zIndex: number;
  swayAmount: number; // How far the bill sways side to side
  swaySpeed: number; // Speed of the sway oscillation
  swayPhase: number; // Current phase of the sway
  initialX: number; // Starting X position for sway reference
  isFlipping: boolean; // Whether this bill should flip
  flipRotation: number; // Current flip rotation (Y-axis rotation)
  flipSpeed: number; // Speed of flipping
}

interface MoneyRainAnimationProps {
  // How many bills fall in total over the animation's lifetime. Capped
  // at MAX_CONCURRENT_BILLS visible on screen at once regardless of how
  // high this is - extra ones drip in over time as earlier ones clear,
  // same as before.
  billCount?: number;
}

const DEFAULT_BILL_COUNT = 21;
const MAX_CONCURRENT_BILLS = 21;

// A one-shot flourish - a handful of bills fall, then the animation clears
// itself and stays idle. Originally played during login; now used as a
// small celebratory overlay on the dashboard when there's money earned
// since the last payment. Portaled onto <body> as a fixed, full-viewport,
// click-through overlay (same reasoning as Navigation's mobile menu: no
// ancestor's overflow/position/stacking-context can interfere, and it
// never has a solid background of its own, so whatever page it's dropped
// onto still shows through underneath the falling bills).
const MoneyRainAnimation: React.FC<MoneyRainAnimationProps> = ({ billCount = DEFAULT_BILL_COUNT }) => {
  const totalBills = Math.max(0, Math.floor(billCount));
  const maxConcurrent = Math.min(totalBills, MAX_CONCURRENT_BILLS);

  const [bills, setBills] = useState<FloatingBill[]>([]);
  const billsCreatedRef = useRef(0);
  const creationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const billCounterRef = useRef(0); // Track total bills created for flipping pattern

  useEffect(() => {
    if (totalBills <= 0) {
      return;
    }

    const createBill = (): FloatingBill => {
      const width = 60 + Math.random() * 60; // 60px to 120px width
      const height = width * 0.43; // Maintain dollar bill proportions (~2.3:1 ratio)
      const xPos = Math.random() * (window.innerWidth - width);

      // Increment bill counter and check if this should be a flipping bill
      billCounterRef.current += 1;
      const shouldFlip = billCounterRef.current % 5 === 0;

      return {
        id: Date.now() + Math.random(),
        x: xPos,
        y: -height - Math.random() * 100 - 25, // Start above screen - spacing halved so bills fall closer together
        width,
        height,
        rotation: (Math.random() - 0.5) * 60, // -30 to 30 degrees
        speed: 5.94 + Math.random() * 2.64, // ~32% faster than the original 4.5-6.5px/frame range (20%, then another 10%)
        rotationSpeed: (Math.random() - 0.5) * 1.5,
        scaleType: 'shrink', // Not used anymore, keeping for interface compatibility
        baseScale: 1,
        currentScale: 1,
        scaleSpeed: 0,
        pulsePhase: 0,
        hasPulsed: false,
        zIndex: Math.random() < 0.5 ? 0 : 100,
        swayAmount: 30 + Math.random() * 50, // Sway 30-70 pixels side to side
        swaySpeed: 0.04 + Math.random() * 0.03,
        swayPhase: Math.random() * Math.PI * 2,
        initialX: xPos,
        isFlipping: shouldFlip,
        flipRotation: 0,
        flipSpeed: 5 + Math.random() * 2
      };
    };

    const addBill = () => {
      if (billsCreatedRef.current >= totalBills) {
        if (creationIntervalRef.current) {
          clearInterval(creationIntervalRef.current);
          creationIntervalRef.current = null;
        }
        return;
      }

      setBills(prev => {
        if (prev.length < maxConcurrent) {
          billsCreatedRef.current += 1;
          return [...prev, createBill()];
        }
        return prev;
      });
    };

    const initialBurst = Math.min(5, totalBills);
    for (let i = 0; i < initialBurst; i++) {
      setTimeout(() => addBill(), i * 100);
    }

    const interval = setInterval(addBill, 400);
    creationIntervalRef.current = interval;

    return () => {
      if (creationIntervalRef.current) {
        clearInterval(creationIntervalRef.current);
      }
    };
  }, [totalBills, maxConcurrent]);

  // Animate bills falling
  useEffect(() => {
    if (totalBills <= 0) {
      return;
    }

    const animationFrame = () => {
      setBills(prev => {
        const updatedBills = prev.map(bill => {
          const newSwayPhase = bill.swayPhase + bill.swaySpeed;
          const swayOffset = Math.sin(newSwayPhase) * bill.swayAmount;
          const newX = bill.initialX + swayOffset;
          const swayInfluencedRotation = bill.rotation + bill.rotationSpeed + (Math.sin(newSwayPhase) * 2);
          const newFlipRotation = bill.isFlipping ? bill.flipRotation + bill.flipSpeed : 0;

          return {
            ...bill,
            x: newX,
            y: bill.y + bill.speed,
            rotation: swayInfluencedRotation,
            swayPhase: newSwayPhase,
            flipRotation: newFlipRotation,
            currentScale: 1
          };
        });

        const billsOnScreen = updatedBills.filter(bill => bill.y < window.innerHeight + 100);

        // Every bill we'll ever create has been created, and nothing from
        // that set is still visible - the show's over, stop the interval
        // entirely instead of ticking forever on an empty array.
        if (billsCreatedRef.current >= totalBills && billsOnScreen.length === 0) {
          if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
            animationIntervalRef.current = null;
          }
          return [];
        }

        return billsOnScreen;
      });
    };

    const interval = setInterval(animationFrame, 16); // ~60fps
    animationIntervalRef.current = interval;
    return () => {
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
      }
    };
  }, [totalBills]);

  if (totalBills <= 0) {
    return null;
  }

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 2000
      }}
      aria-hidden="true"
    >
      {bills.map((bill) => (
        <div
          key={bill.id}
          className="absolute"
          style={{
            width: `${bill.width}px`,
            height: `${bill.height}px`,
            transform: `translate3d(${bill.x}px, ${bill.y}px, 0) rotate(${bill.rotation}deg) rotateX(${bill.flipRotation}deg)`,
            transformStyle: 'preserve-3d',
            zIndex: bill.zIndex
          }}
        >
          {/* Dollar Bill */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              width: '100%',
              height: '100%',
              backgroundColor: '#10b981',
              border: '2px solid #047857',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            {/* Bill background gradient */}
            <div
              className="absolute"
              style={{
                inset: 0,
                background: 'linear-gradient(45deg, #34d399, #10b981, #059669)'
              }}
            ></div>

            {/* Corner decorations - scaled based on size */}
            <div
              className="absolute"
              style={{
                top: `${Math.max(2, bill.width * 0.03)}px`,
                left: `${Math.max(2, bill.width * 0.03)}px`,
                width: `${Math.max(3, bill.width * 0.05)}px`,
                height: `${Math.max(3, bill.width * 0.05)}px`,
                backgroundColor: '#065f46',
                borderRadius: '50%'
              }}
            ></div>
            <div
              className="absolute"
              style={{
                top: `${Math.max(2, bill.width * 0.03)}px`,
                right: `${Math.max(2, bill.width * 0.03)}px`,
                width: `${Math.max(3, bill.width * 0.05)}px`,
                height: `${Math.max(3, bill.width * 0.05)}px`,
                backgroundColor: '#065f46',
                borderRadius: '50%'
              }}
            ></div>
            <div
              className="absolute"
              style={{
                bottom: `${Math.max(2, bill.width * 0.03)}px`,
                left: `${Math.max(2, bill.width * 0.03)}px`,
                width: `${Math.max(3, bill.width * 0.05)}px`,
                height: `${Math.max(3, bill.width * 0.05)}px`,
                backgroundColor: '#065f46',
                borderRadius: '50%'
              }}
            ></div>
            <div
              className="absolute"
              style={{
                bottom: `${Math.max(2, bill.width * 0.03)}px`,
                right: `${Math.max(2, bill.width * 0.03)}px`,
                width: `${Math.max(3, bill.width * 0.05)}px`,
                height: `${Math.max(3, bill.width * 0.05)}px`,
                backgroundColor: '#065f46',
                borderRadius: '50%'
              }}
            ></div>

            {/* Central content - scaled based on size, centered on the bill */}
            <div
              style={{
                position: 'relative',
                zIndex: 10,
                textAlign: 'center',
                fontWeight: 900,
                fontSize: `${Math.max(16, bill.width * 0.22)}px`,
                color: '#064e3b'
              }}
            >
              $10
            </div>

            {/* Border lines - scaled based on size */}
            <div
              className="absolute"
              style={{
                top: `${Math.max(1, bill.width * 0.02)}px`,
                left: `${Math.max(6, bill.width * 0.08)}px`,
                right: `${Math.max(6, bill.width * 0.08)}px`,
                height: '1px',
                backgroundColor: '#064e3b'
              }}
            ></div>
            <div
              className="absolute"
              style={{
                bottom: `${Math.max(1, bill.width * 0.02)}px`,
                left: `${Math.max(6, bill.width * 0.08)}px`,
                right: `${Math.max(6, bill.width * 0.08)}px`,
                height: '1px',
                backgroundColor: '#064e3b'
              }}
            ></div>
            <div
              className="absolute"
              style={{
                top: `${Math.max(6, bill.height * 0.15)}px`,
                bottom: `${Math.max(6, bill.height * 0.15)}px`,
                left: `${Math.max(1, bill.width * 0.02)}px`,
                width: '1px',
                backgroundColor: '#064e3b'
              }}
            ></div>
            <div
              className="absolute"
              style={{
                top: `${Math.max(6, bill.height * 0.15)}px`,
                bottom: `${Math.max(6, bill.height * 0.15)}px`,
                right: `${Math.max(1, bill.width * 0.02)}px`,
                width: '1px',
                backgroundColor: '#064e3b'
              }}
            ></div>
          </div>
        </div>
      ))}
    </div>,
    document.body
  );
};

export default MoneyRainAnimation;
